import { FC, useState, useRef, useEffect } from 'react'
import { Box, TextField, IconButton, CircularProgress } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { InlineEditableFieldProps } from './types'
import { showAlertAction } from '../../store/alerts'
import { useAppDispatch } from '../../hooks'
import { updateEntryField } from '../../services'

/** Ignore only float noise; 0.0001 was too large and skipped real changes (e.g. 1.4455 → 1.44556). */
const NUMBER_EQUAL_EPSILON = 1e-12

function formatForDisplay(
  v: number | string | null | undefined,
  formatter?: (value: number | string) => string
): string {
  if (v === null || v === undefined || v === '') return ''
  if (formatter) return formatter(v as number | string)
  return String(v)
}

export const InlineEditableField: FC<InlineEditableFieldProps> = ({
  value,
  fieldName,
  entryId,
  entity,
  siteId,
  formatter,
  parser,
  validation,
  disabled = false,
  type = 'number',
  min = 0,
  isNegative = false,
  align = 'right',
  onUpdateSuccess,
  dataCy,
}) => {
  const dispatch = useAppDispatch()
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [originalValue, setOriginalValue] = useState<number | string | null | undefined>(
    value
  )
  const [optimisticValue, setOptimisticValue] = useState<number | string | null>(null)
  const [showIconOnLoad, setShowIconOnLoad] = useState<boolean>(true)
  const inputRef = useRef<HTMLInputElement>(null)
  const prevValueRef = useRef<number | string | null | undefined>(value)

  // Update originalValue when value prop changes (but only if not currently editing or saving)
  useEffect(() => {
    if (!isEditing && !isSaving) {
      setOriginalValue(value)
      // If value prop changed and matches optimistic value, clear optimistic value
      if (value !== prevValueRef.current) {
        setOptimisticValue(null)
        prevValueRef.current = value
      }
    }
  }, [value, isEditing, isSaving])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Show icons for 2-3 seconds on page load, then fade to hover-only
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowIconOnLoad(false)
    }, 2500) // 2.5 seconds

    return () => {
      clearTimeout(timer)
    }
  }, [])

  const validateValue = (val: string): boolean => {
    if (validation) {
      const parsed = parser ? parser(val) : val
      return validation(parsed)
    }

    if (type === 'number') {
      const num = parseFloat(val)
      return !isNaN(num) && num >= min
    }
    return val.trim().length > 0
  }

  const handleEditClick = () => {
    if (disabled) return
    // Use optimistic value if available, otherwise use prop value
    const currentValue = optimisticValue !== null ? optimisticValue : value
    // For edit mode, show raw value (not formatted) so user can edit the number directly
    const rawValue =
      currentValue === null || currentValue === undefined || currentValue === ''
        ? ''
        : String(currentValue)
    setEditValue(rawValue)
    setIsEditing(true)
    setOriginalValue(currentValue)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setEditValue('')
  }

  const handleSave = async () => {
    if (editValue === null || editValue === undefined || editValue === '') {
       setIsEditing(false)
       return
    }

    if (!validateValue(editValue)) {
      dispatch(
        showAlertAction({
          message:
            type === 'number'
              ? `Invalid value. Please enter a number >= ${min}.`
              : 'Please enter a valid value.',
          severity: 'error',
        })
      )
      return
    }

    const parsedValue = parser ? parser(editValue) : editValue
    let finalValue = type === 'number' ? parseFloat(String(parsedValue)) : parsedValue

    if(type === 'number' && isNegative) {
       finalValue = 0 - Number(finalValue);
    }

    // Compare with original value to avoid unnecessary API calls
    const originalValueNum: number | null =
      type === 'number'
        ? originalValue === null || originalValue === undefined || originalValue === ''
          ? null
          : Number(originalValue)
        : null
    const finalValueNum = type === 'number' ? Number(finalValue) : null

    if (type === 'number') {
      if (
        originalValueNum !== null &&
        finalValueNum !== null &&
        !Number.isNaN(originalValueNum) &&
        !Number.isNaN(finalValueNum) &&
        Math.abs(originalValueNum - finalValueNum) < NUMBER_EQUAL_EPSILON
      ) {
        setIsEditing(false)
        setEditValue('')
        return
      }
    } else if (String(finalValue) === String(originalValue ?? '')) {
      setIsEditing(false)
      setEditValue('')
      return
    }

    // Last good value shown before this save (parent `value` can still be stale after a prior success)
    const revertOnFailure = optimisticValue !== null ? optimisticValue : value

    // Optimistic update - set optimistic value immediately
    setOptimisticValue(finalValue)
    setIsSaving(true)
    setIsEditing(false)

    try {
      const response = await updateEntryField({
        entryId: entryId,
        fieldName: fieldName,
        entity: entity,
        value: finalValue,
        ...(siteId != null ? { siteId } : {}),
      })

      // Check if response indicates success
      if (response?.data?.success === false) {
        throw new Error(response.data.message || 'Update failed')
      }

      // Success - optimistic value will remain until parent updates value prop
      if (onUpdateSuccess) {
        onUpdateSuccess(finalValue)
      }
    } catch (error: any) {
      // Restore last successful display, not null (null would show stale parent `value`)
      setOptimisticValue(revertOnFailure)
      setIsEditing(false)
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update. Please check your connection.'
      dispatch(
        showAlertAction({
          message: errorMessage,
          severity: 'error',
        })
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation()
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleBlur = () => {
    // Small delay to allow click events to fire first
    setTimeout(() => {
      if (isEditing) {
        handleSave()
      }
    }, 200)
  }

  // Use optimistic value if available, otherwise use prop value
  const displayValue =
    optimisticValue !== null ? optimisticValue : value

  const flexJustify = align === 'left' ? 'flex-start' : 'flex-end'
  const textAlignSx: 'left' | 'right' = align === 'left' ? 'left' : 'right'

  if (disabled) {
    const formatted = formatForDisplay(displayValue, formatter)
    return (
      <Box sx={{ textAlign: textAlignSx }} data-cy={dataCy}>
        {formatted === '' ? '\u00A0' : formatted}
      </Box>
    )
  }

  if (isSaving) {
    const formatted = formatForDisplay(displayValue, formatter)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: flexJustify,
        }}
        data-cy={dataCy}
      >
        <Box sx={{ textAlign: textAlignSx, flex: type === 'text' ? 1 : undefined, minWidth: 0 }}>
          {formatted === '' ? '\u00A0' : formatted}
        </Box>
        <CircularProgress size={16} />
      </Box>
    )
  }

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: flexJustify, width: '100%' }}>
        <TextField
          inputRef={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          onBlur={handleBlur}
          type={type}
          size="small"
          variant="outlined"
          data-cy={dataCy}
          sx={{
            width: type === 'text' ? '100%' : '100px',
            minWidth: type === 'text' ? 160 : undefined,
            maxWidth: type === 'text' ? '100%' : undefined,
            '& .MuiInputBase-root': {
              height: '32px',
            },
            '& .MuiInputBase-input': {
              padding: '6px 8px',
              fontSize: '0.875rem',
              textAlign: textAlignSx,
            },
          }}
          autoFocus
        />
      </Box>
    )
  }

  const formatted = formatForDisplay(displayValue, formatter)

  return (
    <Box
      data-cy={dataCy}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        justifyContent: flexJustify,
        width: '100%',
        '&:hover .edit-icon': {
          opacity: 1,
        },
      }}
    >
      <Box
        sx={{
          textAlign: textAlignSx,
          flex: type === 'text' ? 1 : undefined,
          minWidth: 0,
        }}
      >
        {formatted === '' ? '\u00A0' : formatted}
      </Box>
      <IconButton
        className="edit-icon"
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleEditClick()
        }}
        sx={{
          opacity: showIconOnLoad ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          padding: '4px',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

